import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { FileText, ChevronRight, Paperclip, X, CreditCard, Award, Download } from "lucide-react";
import { toast } from "sonner";
import { Spinner, StatusBadge, EmptyState } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { api, errMsg, fmtINR, fmtDate, uploadFileB64, downloadBlob, fileHref } from "../../lib/api";
import { firstAllowedTab, useRoleModules } from "../../lib/roleModules";
import { NoAccessPanel } from "../../components/FeatureGate";

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

function DynamicForm({ service, onSubmitted, code }) {
  const [data, setData] = useState({});
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(null);
  const [busy, setBusy] = useState(false);
  const schema = [...(service.form_schema || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  const handleFile = async (f, file) => {
    if (!file) return;
    setUploading(f.field_name);
    try {
      const up = await uploadFileB64(code, file);
      setDocs((d) => [...d.filter((x) => x.field !== f.field_name), { ...up, field: f.field_name, label: f.label }]);
      setData((d) => ({ ...d, [f.field_name]: up.filename }));
      toast.success(`${file.name} uploaded`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(null);
    }
  };

  const removeDoc = (field) => {
    setDocs((d) => d.filter((x) => x.field !== field));
    setData((d) => ({ ...d, [field]: "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post(`/t/${code}/applications`, { configured_service_id: service.id, form_data: data, documents: docs });
      toast.success(`Application ${r.data.application_number} submitted`);
      onSubmitted();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      {schema.map((f) => (
        <div key={f.field_name} className="space-y-1">
          <Label>{f.label}{f.required ? " *" : ""}</Label>
          {f.type === "dropdown" ? (
            <Select value={data[f.field_name] || ""} onValueChange={(v) => setData({ ...data, [f.field_name]: v })}>
              <SelectTrigger data-testid={`app-field-${f.field_name}`}><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{(f.options || []).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          ) : f.type === "textarea" ? (
            <Textarea rows={3} value={data[f.field_name] || ""} required={!!f.required}
              onChange={(e) => setData({ ...data, [f.field_name]: e.target.value })}
              data-testid={`app-field-${f.field_name}`} />
          ) : f.type === "file" ? (
            <div>
              {docs.find((d) => d.field === f.field_name) ? (
                <div className="flex items-center justify-between border rounded-lg px-3 py-2 text-sm bg-muted/50">
                  <a href={fileHref(docs.find((d) => d.field === f.field_name).url)} target="_blank" rel="noreferrer"
                    className="flex items-center gap-2 truncate hover:underline underline-offset-2"
                    title="View uploaded document" data-testid={`app-file-view-${f.field_name}`}>
                    <Paperclip className="h-3.5 w-3.5 shrink-0" /> {docs.find((d) => d.field === f.field_name).filename}
                  </a>
                  <button type="button" onClick={() => removeDoc(f.field_name)} data-testid={`app-file-remove-${f.field_name}`}>
                    <X className="h-4 w-4 text-destructive" />
                  </button>
                </div>
              ) : (
                <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx" disabled={uploading === f.field_name}
                  onChange={(e) => handleFile(f, e.target.files?.[0])} data-testid={`app-field-${f.field_name}`} />
              )}
              {uploading === f.field_name && <p className="text-xs text-muted-foreground mt-1">Uploading...</p>}
            </div>
          ) : (
            <Input type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
              value={data[f.field_name] || ""} required={!!f.required}
              onChange={(e) => setData({ ...data, [f.field_name]: e.target.value })}
              data-testid={`app-field-${f.field_name}`} />
          )}
        </div>
      ))}
      {(service.fee_structure?.base_fee || 0) > 0 && (
        <p className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2" data-testid="service-fee-note">
          Service fee: {fmtINR(service.fee_structure.base_fee)} — pay online after submitting, or at the municipal counter.
        </p>
      )}
      <Button type="submit" className="w-full" disabled={busy || !!uploading} data-testid="citizen-service-apply-button">
        {busy ? "Submitting..." : "Submit application"}
      </Button>
    </form>
  );
}

// Phase 152: citizen Services tabs <-> "services" module sub-features
const TABS = [
  { value: "browse", feature: "browse" },
  { value: "mine", feature: "my_applications" },
];

export default function CitizenServices() {
  const { code } = useParams();
  const access = useRoleModules(code);
  const showBrowse = access.featureAllowed("services", "browse");
  const showMine = access.featureAllowed("services", "my_applications");
  // applying creates an application (POST /applications) -> needs full access on "My applications"
  const canApply = access.canWrite("services", "my_applications");
  const visibleTabs = TABS.filter((t) => access.featureAllowed("services", t.feature));
  const [tab, setTab] = useState("browse");
  useEffect(() => {
    if (!access.loaded) return;
    if (!visibleTabs.some((t) => t.value === tab)) { const f = firstAllowedTab(access, "services", TABS); if (f) setTab(f); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [access.loaded, visibleTabs.length]);
  const [services, setServices] = useState(null);
  const [apps, setApps] = useState(null);
  const [applying, setApplying] = useState(null);
  const [payingId, setPayingId] = useState(null);

  const load = useCallback(async () => {
    const [s, a] = await Promise.all([
      showBrowse ? api.get(`/t/${code}/services`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      showMine ? api.get(`/t/${code}/applications`).catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
    ]);
    setServices(s.data);
    setApps(a.data);
  }, [code, showBrowse, showMine]);
  useEffect(() => { load().catch(() => {}); }, [load]);

  const payFee = async (a) => {
    setPayingId(a.id);
    try {
      const r = await api.post(`/t/${code}/applications/${a.id}/pay/order`);
      const ok = await loadRazorpayScript();
      if (!ok) { toast.error("Could not load Razorpay checkout. Check your internet connection."); return; }
      const d = r.data;
      const rzp = new window.Razorpay({
        key: d.key_id, amount: d.amount, currency: d.currency, name: d.name,
        description: d.description, order_id: d.order_id, prefill: d.prefill || {},
        theme: { color: "#1e3a5f" },
        handler: async (resp) => {
          try {
            await api.post(`/t/${code}/applications/${a.id}/pay/verify`, resp);
            toast.success("Service fee paid successfully");
            load();
          } catch (err) {
            toast.error(errMsg(err));
          }
        },
      });
      rzp.open();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setPayingId(null);
    }
  };

  const downloadCert = async (a) => {
    try {
      await downloadBlob(`/t/${code}/applications/${a.id}/certificate`, `${(a.certificate_number || a.application_number).replace(/\//g, "-")}.pdf`);
      toast.success("Certificate downloaded");
    } catch (err) {
      toast.error("Could not download certificate");
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold font-heading">Municipal Services</h1>
        <p className="text-muted-foreground text-sm">Apply for certificates, licenses and services online</p>
      </div>

      {access.loaded && visibleTabs.length === 0 ? <NoAccessPanel what="Municipal Services" testId="services-no-access" /> : (
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {showBrowse && <TabsTrigger value="browse" data-testid="tab-browse-services">Available services</TabsTrigger>}
          {showMine && <TabsTrigger value="mine" data-testid="tab-my-applications">My applications {apps ? `(${apps.length})` : ""}</TabsTrigger>}
        </TabsList>

        <TabsContent value="browse" className="mt-4">
          {!services ? <Spinner /> : services.length === 0 ? (
            <EmptyState title="No services available yet" subtitle="The municipality has not published any online services" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="citizen-services-grid">
              {services.map((s) => (
                <Card key={s.id} className="shadow-sm hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center">
                        <FileText className="h-5 w-5 text-accent-foreground" />
                      </div>
                      <Badge variant="secondary" className="text-[10px]">{s.category}</Badge>
                    </div>
                    <p className="font-semibold mt-3">{s.service_name}</p>
                    <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{s.description}</p>
                    <div className="flex items-center justify-between mt-3">
                      <p className="text-xs text-muted-foreground">SLA {s.sla_days} days {s.fee_structure?.base_fee > 0 ? `• ${fmtINR(s.fee_structure.base_fee)}` : "• Free"}</p>
                      {canApply ? (
                      <Button size="sm" onClick={() => setApplying(s)} data-testid={`apply-service-${s.service_code}`}>
                        Apply <ChevronRight className="h-3.5 w-3.5 ml-1" />
                      </Button>
                      ) : (
                        <span className="text-[11px] text-muted-foreground" data-testid={`apply-service-locked-${s.service_code}`}>Applications disabled</span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="mine" className="mt-4">
          {!apps ? <Spinner /> : apps.length === 0 ? (
            <EmptyState title="No applications yet" subtitle="Apply for a service from the Available services tab" />
          ) : (
            <div className="space-y-3" data-testid="citizen-applications-list">
              {apps.map((a) => (
                <Card key={a.id} className="shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <p className="font-mono text-sm font-medium">{a.application_number}</p>
                        <p className="font-medium text-sm">{a.service_name}</p>
                        <p className="text-xs text-muted-foreground">{fmtDate(a.created_at)}</p>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <StatusBadge status={a.status} />
                        <Badge variant="outline" className="text-[10px] capitalize">Stage: {a.current_stage?.replace(/_/g, " ")}</Badge>
                        {a.payment_required && (
                          <Badge variant={a.payment_status === "paid" ? "secondary" : "destructive"} className="text-[10px]" data-testid={`app-payment-status-${a.application_number}`}>
                            {a.payment_status === "paid" ? `Fee paid ${fmtINR(a.payment_amount)}` : `Fee due ${fmtINR(a.payment_amount)}`}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-wrap mt-3">
                      {(a.workflow_stages || []).map((s, i) => {
                        const curIdx = a.workflow_stages.indexOf(a.current_stage);
                        return (
                          <div key={s} className={`text-[10px] px-2 py-1 rounded-full capitalize ${
                            i < curIdx ? "bg-emerald-50 text-emerald-700" : i === curIdx ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                          }`}>{s.replace(/_/g, " ")}</div>
                        );
                      })}
                    </div>
                    {(a.documents || []).length > 0 && (
                      <div className="flex gap-2 flex-wrap mt-3">
                        {a.documents.map((d, i) => (
                          <a key={i} href={fileHref(d.url)} target="_blank" rel="noreferrer"
                            className="text-xs flex items-center gap-1 border rounded-full px-2.5 py-1 hover:bg-muted"
                            data-testid={`app-document-link-${i}`}>
                            <Paperclip className="h-3 w-3" /> {d.filename || `Document ${i + 1}`}
                          </a>
                        ))}
                      </div>
                    )}
                    {(a.payment_required && a.payment_status !== "paid" && !["rejected", "cancelled"].includes(a.status)) || a.status === "completed" ? (
                      <div className="flex gap-2 flex-wrap mt-3">
                        {a.payment_required && a.payment_status !== "paid" && !["rejected", "cancelled"].includes(a.status) && (
                          <Button size="sm" onClick={() => payFee(a)} disabled={payingId === a.id} data-testid={`app-pay-fee-${a.application_number}`}>
                            <CreditCard className="h-3.5 w-3.5 mr-1.5" /> {payingId === a.id ? "Opening checkout..." : `Pay fee ${fmtINR(a.payment_amount)} online`}
                          </Button>
                        )}
                        {a.status === "completed" && (
                          <Button size="sm" variant="outline" onClick={() => downloadCert(a)} data-testid={`app-certificate-download-${a.application_number}`}>
                            <Award className="h-3.5 w-3.5 mr-1.5" /> Download certificate {a.certificate_number ? `(${a.certificate_number})` : ""}
                          </Button>
                        )}
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
      )}

      <Dialog open={!!applying} onOpenChange={(o) => !o && setApplying(null)}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{applying?.service_name}</DialogTitle>
            <DialogDescription>{applying?.description}</DialogDescription>
          </DialogHeader>
          {applying && <DynamicForm service={applying} code={code} onSubmitted={() => { setApplying(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
